import {AbstractControl, FormArray, ValidatorFn} from '@angular/forms';
import {isEmpty, isNil, once, trim} from 'lodash-es';
import validator from 'validator';
import {BigNumber} from '@ethersproject/bignumber';
import {parseEther} from '@ethersproject/units';
import {NetworkItem} from "@shared/modals/connect-metamask-modal/connect-metamask-modal.interface";
import {DexNamesEnum} from "@shared/enums/dex-names.enum";
import {splitKeys, splitWallets} from "@shared/helpers/split-keys";
import {DEX_VALUES} from "@shared/constants/dex-values";
import {NetworkNamesEnum} from "@shared/enums/networks.enum";
import bs58 from "bs58";
import { EthAccountsInterface } from '@shared/interfaces/eth-accounts.interface';
import Decimal from 'decimal.js';


export class FormValidators {
    static notEmptyOrWhitespace(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);

        if (!isNil(control.value) && !val.length) {
            if (val !== control.value) {
                return {
                    notEmpty: true,
                };
            }
        }

        return null;
    }

    static isEthereumAddress(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        if (isEmpty(val)) {
            return null;
        }

        const regEx = new RegExp('^0x[0-9A-Za-z]{0,64}(::)?[a-zA-Z]{0,8}(::)?[a-zA-Z]{0,8}$');

        const isValid = regEx.test(val.trim());

        if (!isValid) {
            return {
                notEthAddress: true,
            };
        }

        return null;
    }

    static isBase58Address(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        if (isEmpty(val)) {
            return null;
        }

        const regEx = new RegExp('^T[0-9A-Za-z]{31,41}$');

        if (!regEx.test(val.trim())) {
            return {
                notTronAddress: true,
            };
        }

        return null;
    }

    static isEthereumAddresses(control: AbstractControl): { [p: string]: any } | null {
        const val: string[] = splitWallets(control.value);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^0x[0-9A-Za-z]{0,42}(::)?[a-zA-Z]{0,8}(::)?[a-zA-Z]{0,8}$');

        const isValid = val.every((v) => {
            return regEx.test(v.trim());
        });

        if (!isValid) {
            return {
                notEthAddresses: true,
            };
        }

        return null;
    }

    static inExistedWalletsList(list: string[]): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const val: string[] = splitWallets(control.value);
            if (isEmpty(val) || val.length === 0) {
                return null;
            }

            const isValid = val.every((v) => {
                return list.map(v => v.toLowerCase()).includes(v.toLowerCase())
            });

            if (!isValid) {
                return {
                    notInList: true,
                };
            }

            return null;
        }
    }

    static isEthereumKeys(control: AbstractControl): { [p: string]: any } | null {
        const val: string[] = splitKeys(control.value, NetworkNamesEnum.eth);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx1 = new RegExp('^[0-9A-Za-z]{64,80}(::)?[a-zA-Z]{0,8}(::)?[a-zA-Z]{0,8}$');
        const regEx2 = new RegExp('^0x[0-9A-Za-z]{64,80}(::)?[a-zA-Z]{0,8}(::)?[a-zA-Z]{0,8}$');

        const isValid = val.every((v) => {
            if (v.length === 66) {
                return regEx2.test(v.trim());
            }
            return regEx1.test(v.trim());
        });

        if (!isValid) {
            return {
                notEthKeys: true,
            };
        }

        return null;
    }

    static isTonAddresses(control: AbstractControl): { [p: string]: any } | null {
        const val = splitWallets(control.value);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^[0-9A-Za-z+\/_=-]{48}$');

        const isValid = val.every((v) => {
            return regEx.test(v.trim());
        });

        if (!isValid) {
            return {
                notTonAddresses: true,
            };
        }

        return null;
    }

    static isTonKeys(control: AbstractControl): { [p: string]: any } | null {
        const val = splitKeys(control.value, NetworkNamesEnum.ton);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx1 = new RegExp('^[0-9A-Za-z+\/_=-]{86}$');
        const regEx2 = new RegExp('^[0-9A-Za-z+\/_=-]{86}==$');

        const isValid = val.every((v) => {
            if (v?.length === 86) {
                return regEx1.test(v.trim());
            }
            return regEx2.test(v.trim());
        });

        if (!isValid) {
            return {
                notTonKeys: true,
            };
        }

        return null;
    }

    static isValidCexQuoteAddress(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        const quotes = ['USDT', 'USDC', 'DAI', 'TUSD', 'BTC', 'ETH', 'BNB'];
        if (isEmpty(val)) {
            return null;
        }

        if (!quotes.map(quote => quote.toLowerCase()).includes(val.toLowerCase())) {
            return {
                notValidQuote: true,
            };
        }

        return null;
    }

    static hasDuplicate(): ValidatorFn {
        return (formArray: AbstractControl): { [key: string]: any } | null => {
            const array = formArray as FormArray;
            const values = array.controls.map(x => (x.value as String)?.toLowerCase());
            let dublicates = [];
            values.forEach(
                (value, index) => values.forEach((val, i) => {
                    if (index !== i && value === val && val !== '') {
                        dublicates.push(i);
                    }
                })
            );
            if (dublicates.length > 0) {
                dublicates.forEach(index => {
                    array.controls[index].setErrors({ dublicate: 'Has Duplicate !!!' })
                })
            } else {
                array.controls.forEach(control => {
                    if (control.errors && control.errors['dublicate']) {
                        delete control.errors['dublicate'];
                        if (!Object.keys(control.errors).length) {
                            control.setErrors(null);
                        }
                    }
                })
            }

            return !!dublicates.length ? {error: 'Dublicates'} : null;
        };
    }

    static isSolanaAddresses(control: AbstractControl): { [p: string]: any } | null {
        const val: string[] = splitWallets(control.value);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^[1-9A-HJ-NP-Za-km-z]{32,44}$');

        const isValid = val.every((v) => {
            return regEx.test(v.trim());
        });

        if (!isValid) {
            return {
                notSolanaAddresses: true,
            };
        }

        return null;
    }

    static isBase58Addresses(control: AbstractControl): { [p: string]: any } | null {
        const val: string[] = splitWallets(control.value);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^T[0-9A-Za-z]{31,41}$');

        const isValid = val.every((v) => {
            return regEx.test(v);
        });

        if (!isValid) {
            return {
                notTronAddresses: true,
            };
        }

        return null;
    }

    static isSolanaKeys(control: AbstractControl): { [p: string]: any } | null {
        const val = splitKeys(control.value, NetworkNamesEnum.solana);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }


        const isValid = val.every((v) => {
            const array = v?.split(',');

            if (array?.length < 64) {
                return false;
            }
            return array.every(arr => +arr <= 256);
        });

        if (!isValid) {
            return {
                notSolanaKeys: true,
            };
        }

        return null;
    }

    static isASCIIStr(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        if (isEmpty(val)) {
            return null;
        }

        if (!validator.isAscii(val)) {
            return {
                isAscii: true,
            };
        }

        return null;
    }

    static isNotEqualsTo(depControl: string): ValidatorFn {
        const dependentControlProvider = this.getDependentControlProvider(depControl);

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }

            const dependentControl = dependentControlProvider(control);
            const depValue = dependentControl.value;

            if (!control.value) {
                return null;
            }

            if (control.value.trim().toLowerCase() === depValue.trim().toLowerCase()) {
                return {
                    equals: true,
                };
            }

            return null;
        };
    }

    static compareControls(depControl: string, mode: 'lower' | 'higher'): ValidatorFn {

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }
            const dependentControl = control.parent?.get(depControl);
            const depValue = Number(dependentControl?.value?.toString()?.replace(',', '.'));
            if (!depValue && depValue !== 0) {
                return null;
            }

            if (!control.value) {
                return null;
            }

            const value = Number(control.value?.toString()?.replace(',', '.'));

            return mode === 'lower' && (value > depValue) ? {isLowerThan: true} :
                mode === 'higher' && (value < depValue) ? {isHigherThan: true} : null
        };
    }

    static sumControls(depControl: string, mode: 'lower' | 'higher', sum: number): ValidatorFn {

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }
            const dependentControl = control.parent?.get(depControl);
            const depValue = Number(dependentControl?.value?.toString()?.replace(',', '.'));
            if (!depValue && depValue !== 0) {
                return null;
            }

            if (!control.value) {
                return null;
            }

            const value = Number(control.value?.toString()?.replace(',', '.'));

            return mode === 'lower' && (value + depValue) > sum ? {isLowerThanSum: true} :
                mode === 'higher' && (value + depValue) < sum ? {isHigherThanSum: true} : null
        };
    }

    static compareInTimesControls(depControl: string, mode: 'lower' | 'higher'): ValidatorFn {

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }
            const dependentControl = control.parent?.get(depControl);
            const depValue = Number(dependentControl?.value?.toString()?.replace(',', '.'));
            if (!depValue && depValue !== 0) {
                return null;
            }

            if (!control.value) {
                return null;
            }

            const value = Number(control.value?.toString()?.replace(',', '.'));

            return mode === 'lower' && (depValue < value * 3) ? {isMuchHigherThan: true} :
                mode === 'higher' && (depValue * 3 > value ) ? {isMuchLowerThan: true} : null;
        };
    }

    static minDecimal(minValue: string | number) {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const controlValue = control.value?.toString()?.replace(',', '.');

            if (
                !control.parent ||
                isEmpty(controlValue) ||
                !/^\d*\.?\,?\d{0,18}$/gim.test(controlValue)
            ) {
                return null;
            }

            if (controlValue[0] === '-') {
                return {
                    minDecimal: true,
                };
            }

            if (!BigNumber.from(parseEther(controlValue)).gte(parseEther(minValue.toString()))) {
                return {
                    minDecimal: true,
                };
            }

            return null;
        };
    }

    static maxDecimal(maxValue: number) {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const controlValue = control.value?.toString()?.replace(',', '.');

            if (
                !control.parent ||
                isEmpty(controlValue) ||
                !/^\d*\.?\,?\d{0,18}$/gim.test(controlValue)
            ) {
                return null;
            }

            if (controlValue[0] === '-') {
                return {
                    minDecimal: true,
                };
            }

            if (!BigNumber.from(parseEther(controlValue)).lte(parseEther(maxValue.toString()))) {
                return {
                    maxDecimal: true,
                };
            }

            return null;
        };
    }

    static compareDecimal(depControl: string, mode: 'lower' | 'higher'): ValidatorFn {

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }

            const dependentControl = control.parent?.get(depControl);

            if (!control.value || !dependentControl?.value) {
                return null;
            }

            const depValue = dependentControl?.value?.replace(',', '.');
            const controlValue = control.value?.replace(',', '.');

            if (
                !controlValue ||
                !/^\d*\.?\,?\d{0,18}$/gim.test(controlValue) ||
                !/^\d*\.?\,?\d{0,18}$/gim.test(depValue)
            ) {
                return null;
            }

            if (controlValue[0] === '-' || depValue[0] === '-') {
                return null;
            }
            if (mode === 'lower' && BigNumber.from(parseEther(controlValue)).gt(parseEther(depValue))) {
                return {
                    lowerDecimal: true,
                };
            } else if (mode === 'higher' && BigNumber.from(parseEther(controlValue)).lt(parseEther(depValue))) {
                return {
                    higherDecimal: true,
                };
            }

            return null;
        };
    }


    static validateIf(depControl: string, condition, validator: ValidatorFn): ValidatorFn {
        const dependentControlProvider = this.getDependentControlProvider(depControl);

        return (control: AbstractControl): { [p: string]: any } | null => {
            if (!control.parent) {
                return null;
            }

            const dependentControl = dependentControlProvider(control);
            const depValue = dependentControl.value;

            if (condition instanceof Function) {
                if (condition(depValue)) {
                    return null;
                }
            } else {
                if (depValue !== condition) {
                    return null;
                }
            }

            if (!!depValue) {
                return validator(control);
            }

            return null;
        };
    }

    static checkDuplicateKeys(dex?: DexNamesEnum): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const network = dex && DEX_VALUES[dex].networkName;
            const newAddresses: string[] = splitKeys(control.value, network, true);

            if (isEmpty(newAddresses) || newAddresses.length <= 0) {
                return null;
            }

            const addresses = {};
            newAddresses.forEach(
                (address) => {
                    if ((network === NetworkNamesEnum.solana || network === NetworkNamesEnum.solana_dev) && control.value[0] !== '[') {
                        (addresses[bs58.encode(JSON.parse(address))] = (addresses[bs58.encode(JSON.parse(address))] || 0) + 1)
                    } else {
                        (addresses[address.toString()] = (addresses[address.toString()] || 0) + 1)
                    }
                },
            );

            const hasDuplicatedAddresses = Object.values(addresses).some((item: any) => item >= 2);
            const uniqueAddresses: string[] = [];

            if (hasDuplicatedAddresses) {
                return {
                    duplicateAddresses: { addresses, newAddressesCount: uniqueAddresses.length },
                };
            }

            return null;
        };
    }

    static checkDuplicateAddresses(existingAddresses?: string[] | null): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const newAddresses: string[] = splitWallets(control.value);

            if (isEmpty(newAddresses) || newAddresses.length <= 0) {
                return null;
            }

            const addresses = {};
            (existingAddresses? [
                ...newAddresses,
                ...existingAddresses.filter((exAddress) => newAddresses.includes(exAddress)),
            ] : newAddresses).forEach(
                (address) =>
                    (addresses[address.toString()] = (addresses[address.toString()] || 0) + 1),
            );

            const hasDuplicatedAddresses = Object.values(addresses).some((item: any) => item >= 2);
            const uniqueAddresses: string[] = [];
            if (existingAddresses) {
                Object.keys(addresses).forEach((address) => {
                    if (!existingAddresses.some((item) => item === address)) {
                        uniqueAddresses.push(address);
                    }
                });
            }

            if (hasDuplicatedAddresses) {
                return {
                    duplicateAddresses: { addresses, newAddressesCount: uniqueAddresses.length },
                };
            }

            return null;
        };
    }

    static getDependentControlProvider(
        dependentProp: string,
    ): (control: AbstractControl) => AbstractControl {
        const subscribeDependencyChanges = once(
            (dependentControl: AbstractControl, control: AbstractControl) => {
                if (!dependentControl) {
                    return;
                }

                dependentControl.valueChanges.subscribe(() => {
                    control.updateValueAndValidity({ emitEvent: false });
                    if (dependentControl.touched) {
                        control.markAsTouched();
                    }
                });
            },
        );

        // @ts-ignore
        return (control: AbstractControl) => {
            const depControl = control.parent?.get(dependentProp);
            subscribeDependencyChanges(depControl, control);

            return depControl;
        };
    }

    static validateWithDecimals(decimals: number): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null  => {
            const reg = new RegExp(`^-?\\d*\\.?\\d{0,${decimals}}$`);
            const value = typeof control?.value === 'number' ? new Decimal(control.value).toFixed() : control?.value;
            return !reg.test(value) ? {wrongDecimals: true} : null;
        }
    }

    static networksValidators(networkList: NetworkItem[]): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null  => {
            const ifSelected = networkList.some(network => {
                return control?.get([network.name, 'checked'])?.value;
            })
            return ifSelected ? null : {noNetworks: true};
        }
    }

    static checkManyToMany(wallets: EthAccountsInterface[]): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const senderWallets = wallets.map(wallet => wallet.address);
            const receiverWallets = splitWallets(control.value);

            if (!receiverWallets) {
                return null;
            } else if (senderWallets.length !== receiverWallets.length) {
                return { walletsLength: true }
            } else if (senderWallets.some(address => receiverWallets?.includes(address))) {
                return { identicalWallets: true }
            }

            return null
        }
    }

    static isXrpAddress(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        if (isEmpty(val)) {
            return null;
        }

        const regEx = new RegExp('^r[1-9A-HJ-NP-Za-km-z]{24,34}$');

        const isValid = regEx.test(val.trim());

        if (!isValid) {
            return {
                notXrpAddress: true,
            };
        }

        return null;
    }

    static isXrpAddresses(control: AbstractControl): { [p: string]: any } | null {
        const val: string[] = splitWallets(control.value);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^r[1-9A-HJ-NP-Za-km-z]{24,34}$');

        const isValid = val.every((v) => {
            return regEx.test(v.trim());
        });

        if (!isValid) {
            return {
                notXrpAddresses: true,
            };
        }

        return null;
    }

    static isXrpKeys(control: AbstractControl): { [p: string]: any } | null {
        const val = splitKeys(control.value, NetworkNamesEnum.xrpl);

        if (isEmpty(val) || val.length === 0) {
            return null;
        }

        const regEx = new RegExp('^s[1-9A-HJ-NP-Za-km-z]{28,34}$');

        const isValid = val.every((v) => {
            return regEx.test(v.trim());
        });

        if (!isValid) {
            return {
                notXrpKeys: true,
            };
        }

        return null;
    }

    static number(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        const regEx = new RegExp('^-?\\d+(?:[.,]\\d{0,18})?$');
        if (!regEx.test(val)) {
            return {
                number: true,
            }
        }
        return null;
    }

    static notZero(control: AbstractControl): { [p: string]: any } | null {
        const val = trim(control.value);
        const regEx = new RegExp('^0+(\.0+)?$');
        if (regEx.test(val)) {
            return {
                zero: true,
            }
        }
        return null;
    }

    static atLeastOneSwitchOnValidator(keys: string[]): ValidatorFn {
        return (control: AbstractControl): { [p: string]: any } | null => {
            const result = keys.map(key => control.get(key)?.value).some(value => value);
            return result ? null : { atLeastOneRequired: true };
        }
    }
}
